import {
  AudienceClauseProposition,
  LogicClauseProposition,
  LogicPropositionType
} from 'model/questionLogic'
import { ScreeningRule } from 'model/questionnaire'
import { PersistentRespondentChoice } from 'model/persistentRespondentChoice'
import getPersistedRespondentChoices from '../../../utils/getPersistedRespondentChoices'
import {
  PropositionMatcher,
  showEntityBasedOnLogic
} from '../../../utils/displayEntityBasedOnLogic'

// Spelt out rather than left to inference because `.filter` does not narrow on its own.
const isAudienceProposition = (
  proposition: LogicClauseProposition
): proposition is AudienceClauseProposition =>
  proposition.propositionType ===
  LogicPropositionType.AudienceResponseOptionType

// Display logic fails a clause whose source was skipped; a rule reads that source as having produced
// no answer, so its negation holds.
const matchAudienceProposition: PropositionMatcher = (choices, proposition) => {
  if (!isAudienceProposition(proposition)) {
    return false
  }

  const { questionId, responseOptionId } = proposition.proposition

  const wasChosen = choices.some(
    (choice) =>
      choice.questionId === questionId &&
      choice.responseChoiceIds.includes(responseOptionId)
  )

  return proposition.negated ? !wasChosen : wasChosen
}

// Null for a condition this cannot evaluate: empty, or naming a non-audience proposition.
const referencedQuestionIds = (
  condition: LogicClauseProposition[][]
): string[] | null => {
  const propositions = condition.flat()
  const audiencePropositions = propositions.filter(isAudienceProposition)

  if (
    audiencePropositions.length === 0 ||
    audiencePropositions.length !== propositions.length
  ) {
    return null
  }

  return audiencePropositions.map(({ proposition }) => proposition.questionId)
}

/**
 * Only an answer settles a question. Not "behind the respondent": in preview the forward arrow walks
 * past a question without answering it.
 *
 * A question hidden by screener display logic therefore never settles, so a rule naming it never fires.
 * That is deliberate: qualification already works this way — a question nobody saw screens nobody out —
 * and a rule reading "not one of {...}" against a question that was never put to the respondent would
 * otherwise screen out everyone outside the gated subgroup.
 */
const isSettled = (questionId: string, choices: PersistentRespondentChoice[]) =>
  choices.some((choice) => choice.questionId === questionId)

/**
 * Index of the first screening rule that disqualifies the respondent, or null if none does.
 * A rule is evaluated only once every question it references is settled.
 */
export const evaluateScreeningRules = (
  respondentId: string,
  surveyId: string,
  rules: ScreeningRule[] | undefined
): number | null => {
  if (!rules || rules.length === 0) {
    return null
  }

  const choices = getPersistedRespondentChoices(respondentId, surveyId)

  const matchedRuleIndex = rules.findIndex(({ condition }) => {
    const questionIds = referencedQuestionIds(condition)

    if (
      !questionIds ||
      !questionIds.every((questionId) => isSettled(questionId, choices))
    ) {
      return false
    }

    return showEntityBasedOnLogic(
      respondentId,
      surveyId,
      condition,
      choices,
      matchAudienceProposition
    )
  })

  return matchedRuleIndex === -1 ? null : matchedRuleIndex
}
